import {
  useRef,
  useState,
  useEffect,
  useContext,
  useLayoutEffect,
} from "react";
import {
  CommandBarButton,
  IconButton,
  Dialog,
  DialogType,
  Stack,
} from "@fluentui/react";
import {
  DismissRegular,
  SquareRegular,
  ShieldLockRegular,
  ErrorCircleRegular,
} from "@fluentui/react-icons";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import uuid from "react-uuid";

import styles from "./Chat.module.css";
import Azure from "../../assets/Azure.svg";
import Genie from "../../assets/GenieLogo.svg";
import Bard from "../../assets/bard.svg";
import GPT4 from "../../assets/gpt-new.svg";
import GeminiIcon from "../../assets/Gemini-New.svg";
import GrookIcon from "../../assets/Grook.svg";
import CloudeIcon from "../../assets/Cloude.svg";
import GenieDocs from "../../assets/Doc.svg";
import DownloadIcon from "@mui/icons-material/Download";
import { normalizeVoiceAppKey } from "../../utils/voiceApp";

import {
  ChatMessage,
  ConversationRequest,
  conversationApi,
  Citation,
  ToolMessageContent,
  ChatResponse,
  getUserInfo,
  Conversation,
  historyGenerate,
  historyUpdate,
  historyClear,
  ChatHistoryLoadingState,
  CosmosDBStatus,
  ErrorMessage,
  UserSessionData,
  fetchUserImage,
  historyList,
  historyEnsure,
  fetchModelDetails,
  CategoryUseCases,
  callTool,
} from "../../api";
import { Answer } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { ChatHistoryPanel } from "../../components/ChatHistory/ChatHistoryPanel";
import { PromtPanel } from "../../components/Promt/PromtPanel";
import { AppStateContext } from "../../state/AppProvider";
import { useBoolean } from "@fluentui/react-hooks";
import Copyright from "../../components/common/Copyright";
import MessageTime from "./ChatStatus";
import Layout from "../layout/Layout";
import { BackendURL } from "../../../env";
import { head } from "lodash";
import { useRealtimeVoice } from "../../components/Voice/useRealtimeVoice";
const enum messageStatus {
  NotRunning = "Not Running",
  Processing = "Processing",
  Done = "Done",
}

const ChatLoader = ({
  className,
  spinnerClass,
}: {
  className: string;
  spinnerClass: string;
}) => (
  <div className={className}>
    <i className={styles.loaderLogo}>
      <svg
        width="32"
        height="18"
        viewBox="0 0 58 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          id="logopath1"
          d="M55.0589 4.27963C54.4429 4.27963 53.9133 4.0797 53.4702 3.67984C53.0379 3.27997 52.8218 2.77204 52.8218 2.15603C52.8218 1.54002 53.0379 1.02668 53.4702 0.616008C53.9133 0.205336 54.4429 0 55.0589 0C55.6965 0 56.2368 0.205336 56.6799 0.616008C57.123 1.02668 57.3446 1.54002 57.3446 2.15603C57.3446 2.73961 57.123 3.24215 56.6799 3.66363C56.2368 4.0743 55.6965 4.27963 55.0589 4.27963ZM56.9231 22.4843C56.9231 23.5228 56.0812 24.3647 55.0426 24.3647C54.0041 24.3647 53.1622 23.5228 53.1622 22.4843V9.64539C53.1622 8.60684 54.0041 7.76494 55.0426 7.76494C56.0812 7.76494 56.9231 8.60684 56.9231 9.64538V22.4843Z"
        ></path>
        <path
          id="logopath2"
          d="M37.2763 21.3745C37.2683 21.3616 37.2541 21.3535 37.2389 21.3535C37.2148 21.3535 37.1952 21.3731 37.1952 21.3973V30.0221C37.1952 31.1145 36.3096 32 35.2173 32C34.1249 32 33.2393 31.1144 33.2393 30.0221V8.38873C33.2393 7.29634 34.1249 6.4108 35.2173 6.4108H35.6547C36.5055 6.4108 37.1952 7.1005 37.1952 7.95128V9.44848C37.1952 9.47239 37.2146 9.49177 37.2385 9.49177C37.2539 9.49177 37.2682 9.48341 37.276 9.47009C38.6284 7.1567 40.6021 6 43.1972 6C45.4138 6 47.1417 6.78165 48.3807 8.34496C49.6198 9.90827 50.2393 12.0079 50.2393 14.6438C50.2393 17.5651 49.5402 19.9043 48.142 21.6616C46.7438 23.4189 44.8341 24.2976 42.4128 24.2976C40.1928 24.2976 38.4806 23.3232 37.2763 21.3745ZM37.1611 14.4384V16.6122C37.1611 17.8903 37.5646 18.9743 38.3717 19.8644C39.1902 20.7544 40.2246 21.1995 41.475 21.1995C42.9414 21.1995 44.0895 20.6289 44.9194 19.4878C45.7605 18.3353 46.1811 16.7321 46.1811 14.6781C46.1811 12.955 45.789 11.6085 45.0046 10.6386C44.2316 9.65723 43.1801 9.16656 41.8502 9.16656C40.4406 9.16656 39.3038 9.66864 38.4399 10.6728C37.5874 11.677 37.1611 12.9322 37.1611 14.4384Z"
          fill="#0D0D0D"
        ></path>
        <path
          id="logopath3"
          d="M25.9636 23.0058C25.9623 22.9454 25.9369 22.8879 25.8925 22.8452C25.8481 22.8025 25.7882 22.778 25.7254 22.7768L18.6226 22.7775C18.617 22.7775 18.6123 22.7745 18.6067 22.7745C18.5738 22.7726 18.5413 22.767 18.5098 22.7577C18.4886 22.7538 18.4678 22.7484 18.4475 22.7413C18.418 22.7269 18.3904 22.7091 18.3653 22.6882C18.3486 22.6776 18.3327 22.6659 18.3177 22.6531C18.2957 22.6284 18.2766 22.6014 18.2609 22.5726C18.2503 22.5569 18.2407 22.5406 18.2324 22.5237C18.2296 22.5172 18.222 22.5138 18.2212 22.5054L16.095 16.4468C15.987 16.0827 15.9708 15.699 16.0478 15.3277C16.1248 14.9563 16.2927 14.608 16.5376 14.3114L21.3062 8.54369C21.4474 8.37108 21.6283 8.2322 21.8346 8.13792C22.0409 8.04364 22.2671 7.99651 22.4956 8.0002L22.4979 8.00249L22.5003 8.0002L22.5087 8.00211C22.7074 7.99972 22.9047 8.03565 23.0888 8.10775C23.2729 8.17986 23.4401 8.28669 23.5805 8.42194C23.6215 8.46137 23.6601 8.50304 23.696 8.54675L28.4638 14.313C28.7101 14.6124 28.8782 14.9642 28.9541 15.3389C29.03 15.7136 29.0115 16.1003 28.9001 16.4667L27.8977 19.3235C27.8803 19.3742 27.8527 19.4211 27.8163 19.4615C27.78 19.5019 27.7357 19.535 27.6861 19.5588C27.6364 19.5827 27.5823 19.5969 27.5269 19.6006C27.4715 19.6043 27.4159 19.5974 27.3632 19.5803C27.3106 19.5632 27.262 19.5363 27.2202 19.5011C27.1785 19.4659 27.1444 19.4231 27.1199 19.3752C27.0954 19.3273 27.081 19.2752 27.0776 19.2219C27.0741 19.1686 27.0817 19.1152 27.0998 19.0647L28.0962 16.2259C28.1674 15.9856 28.1781 15.7326 28.1273 15.4876C28.0766 15.2426 27.9659 15.0128 27.8044 14.8171L23.0414 9.04787C23.007 9.00751 22.9677 8.97125 22.9243 8.93986V14.1122C23.4502 14.2126 23.9185 14.4976 24.238 14.9116C24.5575 15.3256 24.7054 15.8391 24.6529 16.3521C24.6004 16.8651 24.3513 17.3411 23.954 17.6873C23.5567 18.0335 23.0397 18.2253 22.5035 18.2253C21.9673 18.2253 21.4503 18.0335 21.053 17.6873C20.6557 17.3411 20.4066 16.8651 20.3541 16.3521C20.3016 15.8391 20.4495 15.3256 20.769 14.9116C21.0885 14.4976 21.5568 14.2126 22.0827 14.1122L22.0823 8.93872C22.0391 8.97068 22.0004 9.00788 21.9672 9.0494L17.1978 14.8141C17.0377 15.0072 16.9273 15.2339 16.8758 15.4758C16.8243 15.7177 16.833 15.968 16.9013 16.206L18.9259 21.9692L25.727 21.9669C25.8688 21.9668 26.0092 21.9935 26.1402 22.0456C26.2712 22.0977 26.3903 22.1742 26.4905 22.2706C26.5908 22.367 26.6703 22.4814 26.7245 22.6074C26.7787 22.7333 26.8065 22.8683 26.8064 23.0046L26.8068 24.9599C26.8067 25.2352 26.6929 25.4992 26.4904 25.6939C26.2879 25.8885 26.0134 25.9979 25.727 25.998L16.8519 25.9999C16.796 26.0009 16.7405 25.9911 16.6885 25.9712C16.6366 25.9513 16.5893 25.9217 16.5494 25.884C16.5095 25.8464 16.4778 25.8015 16.4562 25.7519C16.4345 25.7023 16.4233 25.6491 16.4233 25.5954C16.4233 25.5416 16.4344 25.4884 16.456 25.4388C16.4776 25.3892 16.5092 25.3443 16.5491 25.3066C16.589 25.2689 16.6362 25.2392 16.6881 25.2193C16.7401 25.1993 16.7956 25.1895 16.8515 25.1904L25.7266 25.19C25.7579 25.1902 25.789 25.1844 25.818 25.1729C25.8469 25.1615 25.8733 25.1446 25.8954 25.1233C25.9176 25.102 25.9351 25.0767 25.947 25.0489C25.9589 25.021 25.965 24.9912 25.9648 24.961L25.9636 23.0058ZM21.575 17.0354C21.7586 17.2119 21.9926 17.3322 22.2473 17.3809C22.502 17.4296 22.7661 17.4046 23.006 17.309C23.246 17.2135 23.4511 17.0517 23.5953 16.8441C23.7396 16.6364 23.8167 16.3924 23.8167 16.1427C23.8167 15.893 23.7396 15.6489 23.5953 15.4413C23.4511 15.2336 23.246 15.0718 23.006 14.9763C22.7661 14.8807 22.502 14.8557 22.2473 14.9044C21.9926 14.9531 21.7586 15.0734 21.575 15.2499C21.329 15.4868 21.1908 15.8079 21.1908 16.1427C21.1908 16.4774 21.329 16.7985 21.575 17.0354Z"
          fill="#DA2128"
          stroke="#DA2128"
          strokeWidth="0.9"
        ></path>
        <circle
          id="logopathcircle"
          cx="23.2471"
          cy="2.24705"
          r="2.24705"
          fill="#DA2128"
        ></circle>
        <path
          id="logopath5"
          d="M14 25.2513C14 26.2171 13.2171 27 12.2513 27H2C0.895431 27 0 26.1046 0 25V2C0 0.895431 0.895431 0 2 0H2.19647C3.30104 0 4.19647 0.895431 4.19647 2V21.5026C4.19647 22.6071 5.0919 23.5026 6.19647 23.5026H12.2513C13.2171 23.5026 14 24.2855 14 25.2513Z"
          fill="#0D0D0D"
        ></path>
      </svg>
    </i>
    <div className={spinnerClass}></div>
  </div>
);

const getResponseServiceName = (
  answer: any,
  isLiveSearchActive?: boolean,
): string => {
  const aiService = answer?.ai_service?.trim?.() || answer?.ai_service || "";
  const mlService = answer?.ml_service?.trim?.() || answer?.ml_service || "";
  const modelName = answer?.modelname?.trim?.() || answer?.modelname || "";
  const genericServices = new Set(["multimodel", "multi model", "multi-model"]);

  const resolvedService =
    [aiService, mlService].find(
      (service) => service && !genericServices.has(service.toLowerCase()),
    ) ||
    modelName ||
    aiService ||
    mlService ||
    (isLiveSearchActive ? "live_search" : "");

  if (answer?.role === "assistant") {
    console.debug("[Chat badge resolver]", {
      ai_service: aiService,
      ml_service: mlService,
      modelname: modelName,
      resolvedService,
    });
  }

  return resolvedService;
};

const getResponseBadgeClassName = (service: string): string => {
  const normalizedService = service.toLowerCase();

  if (normalizedService.includes("grok")) return "GrokFamily";
  if (
    normalizedService.includes("gemini") ||
    normalizedService.includes("banana")
  ) {
    return "GeminiFamily";
  }
  if (normalizedService.includes("gpt")) return "GPTFamily";

  return service;
};

const getUrlPathExtension = (fileUrl?: string) => {
  if (!fileUrl) {
    return "";
  }

  try {
    return new URL(fileUrl).pathname.split(".").pop()?.toLowerCase() || "";
  } catch {
    return fileUrl.split("?")[0].split(".").pop()?.toLowerCase() || "";
  }
};

const isImageUrl = (fileUrl?: string) =>
  ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(
    getUrlPathExtension(fileUrl),
  );

const getAttachmentDisplayName = (fileUrl?: string) => {
  if (!fileUrl) {
    return "";
  }

  try {
    return decodeURIComponent(new URL(fileUrl).pathname.split("/").pop() || "");
  } catch {
    return decodeURIComponent(fileUrl.split("?")[0].split("/").pop() || "");
  }
};

const getTrimmedAttachmentName = (fileUrl?: string) => {
  const fileName = getAttachmentDisplayName(fileUrl);
  return fileName.length > 50 ? `${fileName.slice(0, 50)}...` : fileName;
};

const renderResponseBadgeContent = (service: string) => {
  const normalizedService = service.toLowerCase();

  if (normalizedService.includes("grok")) {
    return (
      <img
        src={GrookIcon}
        alt={`${service} icon`}
        className={styles.aiIconBrandImage}
      />
    );
  }

  if (
    normalizedService.includes("cloude") ||
    normalizedService.includes("claude")
  ) {
    return (
      <img
        src={CloudeIcon}
        alt={`${service} icon`}
        className={styles.aiIconBrandImage}
      />
    );
  }

  if (
    normalizedService.includes("gemini") ||
    normalizedService.includes("banana")
  ) {
    return (
      <img
        src={GeminiIcon}
        alt={`${service} icon`}
        className={styles.aiIconBrandImage}
      />
    );
  }

  if (normalizedService.includes("gpt")) {
    return (
      <img
        src={GPT4}
        alt={`${service} icon`}
        className={styles.aiIconBrandImage}
      />
    );
  }

  switch (service) {
    case "Lipi4oMini":
      return <span>4oM</span>;
    case "Lipi4o":
      return <span>4o</span>;
    case "DALL-E":
      return <span>DE</span>;
    case "PrivacyPolicyBOT":
      return <span>PB</span>;
    case "Finance":
      return <span>FS</span>;
    case "live_search":
      return <span>WS</span>;
    case "LipiContract":
      return <span>CI</span>;
    case "LipiResume":
      return <span>PF</span>;
    case "LipiSupport":
      return <span>SP</span>;
    case "MappingBuddy":
      return <span>MB</span>;
    case "MultiModel":
      return <span>LO</span>;
    default:
      return <span>{service?.slice(0, 2).toUpperCase() || "AI"}</span>;
  }
};

const Chat = () => {
  const appStateContext = useContext(AppStateContext);
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
  const isAdminPage = location.pathname.startsWith("/admin");
  const [isHistoryLoading, setisHistoryLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUpdatingHistory, setIsUpdatingHistory] = useState<boolean>(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
  const [activeCitation, setActiveCitation] =
    useState<
      [
        content: string,
        id: string,
        title: string,
        filepath: string,
        url: string,
        metadata: string,
      ]
    >();

  // const [theme, setTheme] = useState("light");

  // useEffect(() => {
  //   document.body.setAttribute("data-bs-theme", theme);
  // }, [theme]);

  // const toggleTheme = () => {
  //   setTheme((prev) => (prev === "light" ? "dark" : "light"));
  // };

  const [isCitationPanelOpen, setIsCitationPanelOpen] =
    useState<boolean>(false);
  const abortFuncs = useRef([] as AbortController[]);
  const [showAuthMessage, setShowAuthMessage] = useState<boolean>(true);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [processMessages, setProcessMessages] = useState<messageStatus>(
    messageStatus.NotRunning,
  );
  const [clearingChat, setClearingChat] = useState<boolean>(false);
  const [hideErrorDialog, { toggle: toggleErrorDialog }] = useBoolean(true);
  const [errorMsg, setErrorMsg] = useState<ErrorMessage | null>();
  const [currentQuestion, setCurrentQuestion] = useState<string>("");
  const [pdfurl, SetPdfurl] = useState<URL>();
  const [file, setFile] = useState<URL>();

  const [follup, setFollowup] = useState([""]);
  const [selectedFollowup, setSelectedFollowup] = useState<string>("");
  const [beforeDash, setBeforeDash] = useState<string>("");
  const [afterDash, setAfterDash] = useState<string>("");
  const [allCategoryData, setAllCategoryData] = useState<any[]>([]);
  const [liveVoiceTranscript, setLiveVoiceTranscript] = useState<string>("");
  const [liveAITranscript, setLiveAITranscript] = useState<string>("");
  const [voiceSessionMessages, setVoiceSessionMessages] = useState<
    ChatMessage[]
  >([]);
  const liveVoiceTranscriptRef = useRef<string>("");
  const liveAITranscriptRef = useRef<string>("");
  const voiceTurnCommittedRef = useRef<boolean>(false);
  const latestVoiceImageUrlRef = useRef<string | null>(null);
  const previousSelectedAIRef = useRef<string | null>(null);
  const [selectedType, setSelectedType] = useState<"model" | "app" | null>(
    null,
  );

  const hasEnsuredCosmosDB = useRef(false);

  useEffect(() => {
    const fetchChatHistory = async (): Promise<Conversation[] | null> => {
      try {
        const response = await historyList(
          appStateContext?.state.selectedAI || "",
        );

        appStateContext?.dispatch({
          type: "FETCH_CHAT_HISTORY",
          payload: response || null,
        });

        return response || null;
      } catch (error) {
        appStateContext?.dispatch({
          type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
          payload: ChatHistoryLoadingState.Fail,
        });
        appStateContext?.dispatch({
          type: "FETCH_CHAT_HISTORY",
          payload: null,
        });
        console.error("Error fetching chat history:", error);
        return null;
      }
    };

    const getHistoryEnsure = async () => {
      appStateContext?.dispatch({
        type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
        payload: ChatHistoryLoadingState.Loading,
      });

      try {
        const response = await historyEnsure();
        if (response?.cosmosDB) {
          const chatHistory = await fetchChatHistory();
          appStateContext?.dispatch({
            type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
            payload: chatHistory
              ? ChatHistoryLoadingState.Success
              : ChatHistoryLoadingState.Fail,
          });
          appStateContext?.dispatch({
            type: "SET_COSMOSDB_STATUS",
            payload: response,
          });
        } else {
          appStateContext?.dispatch({
            type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
            payload: ChatHistoryLoadingState.Fail,
          });
          appStateContext?.dispatch({
            type: "SET_COSMOSDB_STATUS",
            payload: response,
          });
        }
      } catch (error) {
        appStateContext?.dispatch({
          type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
          payload: ChatHistoryLoadingState.Fail,
        });
        appStateContext?.dispatch({
          type: "SET_COSMOSDB_STATUS",
          payload: { cosmosDB: false, status: CosmosDBStatus.NotConfigured },
        });
      }
    };

    if (!hasEnsuredCosmosDB.current) {
      getHistoryEnsure().then(() => {
        hasEnsuredCosmosDB.current = true;
      });
    } else {
      fetchChatHistory();
    }
  }, [appStateContext?.state.selectedAI]);

  useEffect(() => {
    const fetchExpertise = async () => {
      try {
        const response = await CategoryUseCases();
        setAllCategoryData(response);
      } catch (error) {
        console.error("Error fetching expertise:", error);
      }
    };

    fetchExpertise();
  }, []);

  const errorDialogContentProps = {
    type: DialogType.close,
    title: errorMsg?.title,
    closeButtonAriaLabel: "Close",
    subText: errorMsg?.subtitle,
  };

  const modalProps = {
    titleAriaId: "labelId",
    subtitleAriaId: "subTextId",
    isBlocking: true,
    styles: { main: { maxWidth: 450 } },
  };

  useEffect(() => {
    const fetchAndSetModels = async () => {
      try {
        const sortedModels = await fetchModelDetails();

        appStateContext?.dispatch({
          type: "SET_MODEL_DETAILS",
          payload: sortedModels,
        });

        const modelOnly = sortedModels.filter(
          (m: any) => m.Category === "Model",
        );
        const appOnly = sortedModels.filter((m: any) => m.Category === "App");

        appStateContext?.dispatch({
          type: "SET_MODEL_CATEGORY",
          payload: modelOnly,
        });

        appStateContext?.dispatch({
          type: "SET_APP_CATEGORY",
          payload: appOnly,
        });
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    };

    fetchAndSetModels();
  }, []);

  useEffect(() => {
    const aiService: any = appStateContext?.state.selectedAI;
    const allModels: any[] = appStateContext?.state.modelDetails || [];

    if (aiService && allModels.length > 0) {
      const selectedModel = allModels.find((m: any) => m.Name === aiService);

      if (selectedModel?.Initial_Heading) {
        const [before, after] = selectedModel.Initial_Heading.split("–");
        setBeforeDash(before?.trim() || "");
        setAfterDash(after?.trim() || "");
      }
    }
  }, [appStateContext?.state.selectedAI, appStateContext?.state.modelDetails]);

  const [image, setImage] = useState(
    localStorage.getItem("profileImage") || "",
  );

  useEffect(() => {
    if (!image) {
      fetchUserImage().then(setImage);

      localStorage.setItem("profileImage", image || "");
    }
  }, []);

  useEffect(() => {
    if (
      appStateContext?.state.isCosmosDBAvailable?.status ===
        CosmosDBStatus.NotWorking &&
      appStateContext.state.chatHistoryLoadingState ===
        ChatHistoryLoadingState.Fail &&
      hideErrorDialog
    ) {
      let subtitle = `${appStateContext.state.isCosmosDBAvailable.status}. Please contact the site administrator.`;
      setErrorMsg({
        title: "Chat history is not enabled",
        subtitle: subtitle,
      });
      toggleErrorDialog();
    }
  }, [appStateContext?.state.isCosmosDBAvailable]);

  const handleErrorDialogClose = () => {
    toggleErrorDialog();
    setTimeout(() => {
      setErrorMsg(null);
    }, 500);
  };

  const getUserInfoList = async () => {
    const Url = window.location.href;
    const Email = localStorage.getItem("email");

    // const response = await fetch(`${BackendURL}/receive_token`, {
    //   method: "POST",
    //   headers: {
    //     "Content-Type": "application/json",
    //   },
    //   body: JSON.stringify({ token: { Email: Email } }),
    // });

    const userInfoList = await getUserInfo();

    if (userInfoList.length === 0 && window.location.hostname !== "127.0.0.1") {
      setShowAuthMessage(true);
    } else {
      setShowAuthMessage(false);
    }
  };

  const selectedVoiceAppName = (() => {
    const selectedAppName = appStateContext?.state.selectedApp || "";
    const selectedAIName = appStateContext?.state.selectedAI || "";
    const appCategory = appStateContext?.state.appCategory || [];
    const modelCategory = appStateContext?.state.modelCategory || [];

    const matchedAppConfig =
      appCategory.find(
        (app: any) =>
          app?.Name === selectedAppName ||
          app?.Name === selectedAIName ||
          app?.Display_Name === selectedAppName ||
          app?.Display_Name === selectedAIName,
      ) || null;

    const matchedModelConfig =
      modelCategory.find(
        (model: any) =>
          model?.Name === selectedAIName ||
          model?.Display_Name === selectedAIName,
      ) || null;

    const voiceAppCandidates = [
      selectedAppName,
      selectedAIName,
      matchedAppConfig?.Display_Name,
      matchedAppConfig?.Name,
      matchedModelConfig?.Display_Name,
      matchedModelConfig?.Name,
    ].filter(Boolean);

    return (
      voiceAppCandidates.find((candidate) => normalizeVoiceAppKey(candidate)) ||
      selectedAppName ||
      selectedAIName
    );
  })();

  const extractVoiceToolImageUrl = (result: any): string | null => {
    const directImageUrl =
      typeof result?.image_url === "string"
        ? result.image_url
        : typeof result?.imageUrl === "string"
        ? result.imageUrl
        : null;

    if (directImageUrl?.trim()) {
      return directImageUrl.trim();
    }

    const nestedStringCandidates = [
      result?.result,
      result?.content,
      result?.output_text,
    ];

    for (const candidate of nestedStringCandidates) {
      if (typeof candidate !== "string") {
        continue;
      }

      const trimmedCandidate = candidate.trim();
      if (/^https?:\/\//i.test(trimmedCandidate)) {
        return trimmedCandidate;
      }
    }

    const contentBlocks = Array.isArray(result?.content) ? result.content : [];
    for (const block of contentBlocks) {
      if (
        typeof block?.text === "string" &&
        /^https?:\/\//i.test(block.text.trim())
      ) {
        return block.text.trim();
      }
    }

    return null;
  };

  const { voiceStatus, toggleSession } = useRealtimeVoice(
    (transcript: React.SetStateAction<string>) => {
      const nextTranscript = String(transcript || "").trim();
      if (nextTranscript) {
        voiceTurnCommittedRef.current = false;
        latestVoiceImageUrlRef.current = null;
      }
      liveVoiceTranscriptRef.current = nextTranscript;
      setLiveVoiceTranscript(nextTranscript);
      liveAITranscriptRef.current = "";
      setLiveAITranscript("");
    },
    async (name: string, args: any, callId: any) =>
      callTool(name, args, selectedVoiceAppName),
    (delta: string, done: boolean, replace = false) => {
      if (done) {
        if (voiceTurnCommittedRef.current) {
          return;
        }

        const finalizedUserTranscript = liveVoiceTranscriptRef.current.trim();
        const finalizedAITranscript = String(
          (replace && delta ? delta : liveAITranscriptRef.current) || "",
        ).trim();
        const latestImageUrl = latestVoiceImageUrlRef.current;
        const isTranscriptOnlyImageLink =
          Boolean(finalizedAITranscript) &&
          Boolean(latestImageUrl) &&
          finalizedAITranscript === latestImageUrl;

        if (
          finalizedUserTranscript ||
          (finalizedAITranscript && !isTranscriptOnlyImageLink)
        ) {
          voiceTurnCommittedRef.current = true;
          setVoiceSessionMessages((prev) => [
            ...prev,
            ...(finalizedUserTranscript
              ? [
                  {
                    id: uuid(),
                    role: "user",
                    content: finalizedUserTranscript,
                    date: new Date().toISOString(),
                    createdAt: undefined,
                  },
                ]
              : []),
            ...(finalizedAITranscript && !isTranscriptOnlyImageLink
              ? [
                  {
                    id: uuid(),
                    role: "assistant",
                    content: finalizedAITranscript,
                    date: new Date().toISOString(),
                    createdAt: undefined,
                  },
                ]
              : []),
          ]);
        }
        liveAITranscriptRef.current = "";
        liveVoiceTranscriptRef.current = "";
        setLiveAITranscript("");
        setLiveVoiceTranscript("");
        return;
      }
      if (!delta) return;
      if (replace) {
        liveAITranscriptRef.current = delta;
        setLiveAITranscript(delta);
      } else {
        setLiveAITranscript((prev) => {
          const nextValue = `${prev}${delta}`;
          liveAITranscriptRef.current = nextValue;
          return nextValue;
        });
      }
    },
    (result: any) => {
      if (normalizeVoiceAppKey(selectedVoiceAppName) !== "lipione") {
        return;
      }

      const imageUrl = extractVoiceToolImageUrl(result);
      const content = typeof result?.content === "string" ? result.content : "";

      if (!imageUrl && !content.trim()) {
        return;
      }

      // Text responses are already streamed/finalized through the transcript
      // pipeline, so only persist direct tool payloads like images here.
      if (!imageUrl) {
        return;
      }

      latestVoiceImageUrlRef.current = imageUrl;

      setVoiceSessionMessages((prev) => [
        ...prev,
        {
          id: uuid(),
          role: "assistant",
          content: content || imageUrl || "",
          date: new Date().toISOString(),
          createdAt: undefined,
          isImage: Boolean(imageUrl),
          imageUrl,
          image_url: imageUrl || undefined,
        },
      ]);
    },
    selectedVoiceAppName,
  );

  useEffect(() => {
    liveVoiceTranscriptRef.current = liveVoiceTranscript;
  }, [liveVoiceTranscript]);

  useEffect(() => {
    liveAITranscriptRef.current = liveAITranscript;
  }, [liveAITranscript]);

  useEffect(() => {
    if (voiceStatus === "idle" || voiceStatus === "error") {
      voiceTurnCommittedRef.current = false;
      latestVoiceImageUrlRef.current = null;
      setLiveVoiceTranscript("");
      setLiveAITranscript("");
    }
  }, [voiceStatus]);

  const hasLiveVoicePreview =
    voiceStatus !== "idle" ||
    voiceSessionMessages.length > 0 ||
    Boolean(liveVoiceTranscript.trim()) ||
    Boolean(liveAITranscript.trim());

  const normalizePreviewText = (value?: string | null) =>
    String(value || "")
      .replace(/\s+/g, " ")
      .trim();

  const dedupeConsecutiveAssistantMessages = (items: ChatMessage[]) => {
    return items.reduce<ChatMessage[]>((acc, message) => {
      const previousMessage = acc[acc.length - 1];
      const previousImage =
        previousMessage?.imageUrl || previousMessage?.image_url || null;
      const currentImage = message.imageUrl || message.image_url || null;

      const isDuplicateAssistantMessage =
        previousMessage?.role === "assistant" &&
        message.role === "assistant" &&
        normalizePreviewText(previousMessage.content) ===
          normalizePreviewText(message.content) &&
        previousImage === currentImage;

      if (isDuplicateAssistantMessage) {
        return acc;
      }

      acc.push(message);
      return acc;
    }, []);
  };

  const isVoiceMessageMirroredInChat = (voiceMessage: ChatMessage) => {
    const voiceContent = normalizePreviewText(voiceMessage.content);
    const voiceImage = voiceMessage.imageUrl || voiceMessage.image_url || null;

    return messages.some((chatMessage) => {
      const chatContent = normalizePreviewText(chatMessage.content);
      const chatImage = chatMessage.imageUrl || chatMessage.image_url || null;

      return (
        chatMessage.role === voiceMessage.role &&
        chatContent === voiceContent &&
        chatImage === voiceImage
      );
    });
  };

  const visibleVoiceSessionMessages = voiceSessionMessages.filter(
    (message) => !isVoiceMessageMirroredInChat(message),
  );

  const lastVoiceSessionMessage =
    visibleVoiceSessionMessages.length > 0
      ? visibleVoiceSessionMessages[visibleVoiceSessionMessages.length - 1]
      : null;
  const shouldRenderLiveAIPreview =
    Boolean(liveAITranscript.trim()) &&
    !(
      lastVoiceSessionMessage?.role === "assistant" &&
      lastVoiceSessionMessage.content?.trim() === liveAITranscript.trim()
    );

  const shouldShowPromptPanel =
    !hasLiveVoicePreview && (!messages || messages.length < 1);

  const consumeVoiceSessionMessages = () => {
    const committedVoiceMessages = dedupeConsecutiveAssistantMessages(
      voiceSessionMessages.filter((message) => {
        const content = String(message.content || "").trim();
        const imageUrl = message.imageUrl || message.image_url || "";
        return Boolean(content || imageUrl);
      }),
    );

    if (!committedVoiceMessages.length) {
      return [] as ChatMessage[];
    }

    setVoiceSessionMessages([]);
    voiceTurnCommittedRef.current = false;
    latestVoiceImageUrlRef.current = null;
    liveVoiceTranscriptRef.current = "";
    liveAITranscriptRef.current = "";
    setLiveVoiceTranscript("");
    setLiveAITranscript("");

    return committedVoiceMessages;
  };

  const makeApiRequestWithoutCosmosDB = async (
    question: string,
    conversationId?: string,
  ) => {
    //console.log("makeApiRequestWithoutCosmosDB called with:", { question, conversationId });

    setIsLoading(true);
    setShowLoadingMessage(true);
    const abortController = new AbortController();
    abortFuncs.current.unshift(abortController);

    const userMessage: ChatMessage = {
      id: uuid(),
      role: "user",
      content: question,
      date: new Date().toISOString(),
      createdAt: undefined,
    };
    setCurrentQuestion(question);
    const committedVoiceMessages = consumeVoiceSessionMessages();
    let conversation: Conversation | null | undefined;

    if (!conversationId) {
      conversation = {
        id: conversationId ?? uuid(),
        title: question,
        messages: [...committedVoiceMessages, userMessage],
        date: new Date().toISOString(),
      };
      // console.log("New conversation created:", conversation);
    } else {
      conversation = appStateContext?.state?.currentChat;
      //  console.log("Existing conversation fetched:", conversation);
      if (!conversation) {
        console.error("Conversation not found.");
        setIsLoading(false);
        setShowLoadingMessage(false);
        abortFuncs.current = abortFuncs.current.filter(
          (a) => a !== abortController,
        );
        return;
      } else {
        conversation.messages.push(...committedVoiceMessages, userMessage);
        //   console.log("User message appended to conversation:", userMessage);
      }
    }

    appStateContext?.dispatch({
      type: "UPDATE_CURRENT_CHAT",
      payload: conversation,
    });
    setMessages(dedupeConsecutiveAssistantMessages(conversation.messages));
    //console.log("Updated messages state:", conversation.messages);

    const request: ConversationRequest = {
      messages: [
        ...conversation.messages.filter((answer) => answer.role !== "error"),
      ],
    };

    let result = {} as ChatResponse;

    try {
      let aiService: any = appStateContext?.state.selectedAI;
      let subaiService: any = appStateContext?.state.subselectedAI;
      // console.log("Calling conversationApi with:", { aiService, subaiService, request });

      const response = await conversationApi(
        aiService,
        subaiService,
        request,
        abortController.signal,
      );

      if (response?.body) {
        const reader = response.body.getReader();
        let runningText = "";
        let lastProcessedLength = 0; // Track messages already added

        while (true) {
          setProcessMessages(messageStatus.Processing);
          const { done, value } = await reader.read();
          if (done) break;

          var text = new TextDecoder("utf-8").decode(value);
          const objects = text.split("\n");
          objects.forEach((obj) => {
            try {
              runningText += obj;
              result = JSON.parse(runningText);
              result.choices[0].messages.forEach((obj) => {
                obj.id = uuid();
                obj.date = new Date().toISOString();

                // Handle image_url in response - extract and format for display
                if (obj.image_url && !obj.content) {
                  obj.content = obj.image_url; // Set image URL as content for display
                } else if (obj.image_url && obj.content) {
                  // If both exist, prioritize the image URL for image responses
                  if (!obj.content.match(/\.(png|jpe?g|gif|webp|bmp|svg)/i)) {
                    obj.content = obj.image_url;
                  }
                }
              });

              // 🔧 FIX: Only add NEW messages, not all messages again
              const newMessages =
                result.choices[0].messages.slice(lastProcessedLength);
              if (newMessages.length > 0) {
                setShowLoadingMessage(false);
                setMessages((prev) =>
                  dedupeConsecutiveAssistantMessages([...prev, ...newMessages]),
                );
                lastProcessedLength = result.choices[0].messages.length;
              }

              runningText = "";
            } catch (e) {
              // JSON parse incomplete, wait for more chunks
            }
          });
        }

        // Process messages to handle image URLs - Reference Format
        result.choices[0].messages.forEach((msg) => {
          // ✅ IMAGE HANDLING - Following reference script format
          if (msg.image_url) {
            // Mark message as image for proper display
            msg.isImage = true;
            msg.imageUrl = msg.image_url;

            // Only set content to image_url if no other content
            if (!msg.content) {
              msg.content = msg.image_url;
            }
          }
        });

        result.choices[0].messages.forEach((msg, idx) => {});
        console.log("═".repeat(70) + "\n");

        // 🔧 FIX: Use conversation object which has all accumulated messages
        conversation.messages.push(...result.choices[0].messages);
        //  console.log("Final conversation messages after API response:", conversation.messages);

        appStateContext?.dispatch({
          type: "UPDATE_CURRENT_CHAT",
          payload: conversation,
        });

        // 🔧 FIX: Set final state from conversation which is single source of truth
        const dedupedConversationMessages = dedupeConsecutiveAssistantMessages(
          conversation.messages,
        );
        conversation.messages = dedupedConversationMessages;
        setMessages(dedupedConversationMessages);
      }
    } catch (e) {
      console.error("Error during API request:", e);

      if (!abortController.signal.aborted) {
        let errorMessage =
          "An error occurred. Please try again. If the problem persists, please contact the site administrator.";
        if (result.error?.message) {
          errorMessage = result.error.message;
        } else if (typeof result.error === "string") {
          errorMessage = result.error;
        }
        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: "error",
          content: errorMessage,
          date: new Date().toISOString(),
          createdAt: undefined,
        };
        conversation.messages.push(errorChatMsg);
        appStateContext?.dispatch({
          type: "UPDATE_CURRENT_CHAT",
          payload: conversation,
        });
        setMessages([...messages, errorChatMsg]);
      } else {
        setMessages([...messages, userMessage]);
      }
    } finally {
      setIsLoading(false);
      setShowLoadingMessage(false);
      abortFuncs.current = abortFuncs.current.filter(
        (a) => a !== abortController,
      );
      setProcessMessages(messageStatus.Done);
      // console.log("makeApiRequestWithoutCosmosDB completed.");
    }

    return abortController.abort();
  };

  const makeApiRequestWithCosmosDB = async (
    question: string,
    fileurl: any,
    conversationId?: string,
  ) => {
    // console.log("makeApiRequestWithCosmosDB called with:", { question, fileurl, conversationId });

    setIsLoading(true);
    setShowLoadingMessage(true);
    SetPdfurl(fileurl);

    const abortController = new AbortController();
    abortFuncs.current.unshift(abortController);
    const urlPattern = /https?:\/\/[^\s]+/;
    const isUploadedFileUrl =
      typeof fileurl === "string" && urlPattern.test(fileurl);
    const isimage = isUploadedFileUrl && isImageUrl(fileurl);
    const isPdf = isUploadedFileUrl && !isimage;
    const Email = localStorage.getItem("email");
    const liveSearch = appStateContext?.state.isLiveSeachActive;
    const model = appStateContext?.state.selectedAI;

    const userMessage: ChatMessage = {
      id: uuid(),
      role: "user",
      content: question,
      date: new Date().toISOString(),
      ispdf: isPdf,
      pdfurl: fileurl,
      isimage: isimage,
      Email: Email,
      is_search_active: liveSearch,
      createdAt: undefined,
      modelname: model,
    };

    // console.log("User message object created:", userMessage);
    setCurrentQuestion(question);
    const committedVoiceMessages = consumeVoiceSessionMessages();

    let request: ConversationRequest;
    let conversation;

    if (conversationId) {
      const currentChat = appStateContext?.state.currentChat;
      conversation = currentChat
        ? {
            ...currentChat,
            messages: [
              ...currentChat.messages,
              ...committedVoiceMessages,
              userMessage,
            ],
          }
        : currentChat;
      //console.log("Existing conversation fetched:", conversation);

      if (!conversation) {
        console.error("Conversation not found.");
        setIsLoading(false);
        setShowLoadingMessage(false);
        abortFuncs.current = abortFuncs.current.filter(
          (a) => a !== abortController,
        );
        return;
      } else {
        appStateContext?.dispatch({
          type: "UPDATE_CURRENT_CHAT",
          payload: conversation,
        });
        setMessages(dedupeConsecutiveAssistantMessages(conversation.messages));

        request = {
          messages: [
            ...conversation.messages.filter(
              (answer) => answer.role !== "error",
            ),
          ],
        };
      }
    } else {
      request = {
        messages: [...committedVoiceMessages, userMessage].filter(
          (answer) => answer.role !== "error",
        ),
      };
      setMessages(request.messages);
    }
    let result = {} as ChatResponse;
    try {
      let selectedAI: any = appStateContext?.state.selectedAI;
      let model: any[] = appStateContext?.state.modelCategory || [];
      let app: any[] = appStateContext?.state.appCategory || [];
      let subaiService: any = appStateContext?.state.subselectedAI;

      let aiService = "";

      if (model.some((m: any) => m.Name === selectedAI)) {
        aiService = "MultiModel";
      } else if (app.some((a: any) => a.Name === selectedAI)) {
        aiService = selectedAI;
      }

      let message_id: any = uuid();
      const response = conversationId
        ? await historyGenerate(
            aiService,
            subaiService,
            message_id,
            request,
            abortController.signal,
            conversationId,
          )
        : await historyGenerate(
            aiService,
            subaiService,
            message_id,
            request,
            abortController.signal,
          );

      if (!response?.ok) {
        console.error("Response not OK:", response);
        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: "error",
          content:
            "There was an error generating a response. Chat history can't be saved at this time. If the problem persists, please contact the site administrator.",
          date: new Date().toISOString(),
          createdAt: undefined,
        };

        let resultConversation;
        if (conversationId) {
          resultConversation = appStateContext?.state?.chatHistory?.find(
            (conv) => conv.id === conversationId,
          );
          if (!resultConversation) {
            console.error("Conversation not found.");
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(
              (a) => a !== abortController,
            );
            return;
          }
          resultConversation.messages.push(errorChatMsg);
        } else {
          setMessages([...messages, userMessage, errorChatMsg]);
          setIsLoading(false);
          setShowLoadingMessage(false);
          abortFuncs.current = abortFuncs.current.filter(
            (a) => a !== abortController,
          );
          return;
        }
        appStateContext?.dispatch({
          type: "UPDATE_CURRENT_CHAT",
          payload: resultConversation,
        });
        const dedupedErrorConversationMessages =
          dedupeConsecutiveAssistantMessages(resultConversation.messages);
        resultConversation.messages = dedupedErrorConversationMessages;
        setMessages([...dedupedErrorConversationMessages]);
        return;
      }

      if (response?.body) {
        const reader = response.body.getReader();
        let runningText = "";
        let lastProcessedLength = 0; // 🔧 FIX: Track messages already added

        while (true) {
          setProcessMessages(messageStatus.Processing);
          const { done, value } = await reader.read();
          if (done) break;

          var text = new TextDecoder("utf-8").decode(value);
          const objects = text.split("\n");
          objects.forEach((obj) => {
            try {
              runningText += obj;
              result = JSON.parse(runningText);
              result.choices[0].messages.forEach((obj) => {
                obj.id = uuid();
                obj.date = new Date().toISOString();
              });

              if (result.followups) {
                appStateContext?.dispatch({
                  type: "PRIVACY_FOLLOWUP",
                  payload: result.followups,
                });
                setFollowup(result.followups);
              }

              if (result.files) {
                setFile(result.files);
                // console.log("Files from API:", result.files);
              }

              setShowLoadingMessage(false);

              // 🔧 FIX: Only add NEW messages, not all messages again
              const newMessages =
                result.choices[0].messages.slice(lastProcessedLength);
              if (newMessages.length > 0) {
                if (!conversationId) {
                  setMessages((prev) =>
                    dedupeConsecutiveAssistantMessages([
                      ...prev,
                      ...newMessages,
                    ]),
                  );
                } else {
                  setMessages((prev) =>
                    dedupeConsecutiveAssistantMessages([
                      ...prev,
                      ...newMessages,
                    ]),
                  );
                }
                lastProcessedLength = result.choices[0].messages.length;
              }

              //  console.log("Received and parsed message chunk:", result.choices[0].messages);
              runningText = "";
            } catch (e) {
              // JSON parse incomplete, wait for more chunks
            }
          });
        }

        let resultConversation;
        if (conversationId) {
          resultConversation = conversation;
          if (!resultConversation) {
            console.error("Conversation not found.");
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(
              (a) => a !== abortController,
            );
            return;
          }
          resultConversation.messages.push(...result.choices[0].messages);
        } else {
          resultConversation = {
            id: result.history_metadata.conversation_id,
            title: result.history_metadata.title,
            messages: [...committedVoiceMessages, userMessage],
            date: result.history_metadata.date,
          };
          resultConversation.messages.push(...result.choices[0].messages);
        }

        if (!resultConversation) {
          setIsLoading(false);
          setShowLoadingMessage(false);
          abortFuncs.current = abortFuncs.current.filter(
            (a) => a !== abortController,
          );
          return;
        }

        resultConversation.messages = dedupeConsecutiveAssistantMessages(
          resultConversation.messages,
        );

        appStateContext?.dispatch({
          type: "UPDATE_CURRENT_CHAT",
          payload: resultConversation,
        });

        // 🔧 FIX: Use conversation as single source of truth
        setMessages(resultConversation.messages);
        //console.log("Final conversation messages after API response:", resultConversation.messages);
      }
    } catch (e) {
      console.error("Error during API request:", e);
      if (!abortController.signal.aborted) {
        let errorMessage =
          "An error occurred. Please try again. If the problem persists, please contact the site administrator.";
        if (result.error?.message) {
          errorMessage = result.error.message;
        } else if (typeof result.error === "string") {
          errorMessage = result.error;
        }
        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: "error",
          content: errorMessage,
          date: new Date().toISOString(),
          createdAt: undefined,
        };
        let resultConversation;
        if (conversationId) {
          resultConversation = conversation;
          if (!resultConversation) {
            console.error("Conversation not found.");
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(
              (a) => a !== abortController,
            );
            return;
          }
          resultConversation.messages.push(errorChatMsg);
        } else {
          if (!result.history_metadata) {
            console.warn(
              "No history metadata found — using fallback conversation.",
            );

            resultConversation = {
              id: uuid(),
              title: "New Conversation",
              messages: [...committedVoiceMessages, userMessage],
              date: new Date().toISOString(),
            };
          } else {
            resultConversation = {
              id: result.history_metadata.conversation_id,
              title: result.history_metadata.title,
              messages: [...committedVoiceMessages, userMessage],
              date: result.history_metadata.date,
            };
          }

          resultConversation = {
            id: result.history_metadata.conversation_id,
            title: result.history_metadata.title,
            messages: [...committedVoiceMessages, userMessage],
            date: result.history_metadata.date,
          };
          resultConversation.messages.push(errorChatMsg);
        }
        if (!resultConversation) {
          setIsLoading(false);
          setShowLoadingMessage(false);
          abortFuncs.current = abortFuncs.current.filter(
            (a) => a !== abortController,
          );
          return;
        }
        appStateContext?.dispatch({
          type: "UPDATE_CURRENT_CHAT",
          payload: resultConversation,
        });
        setMessages([...messages, errorChatMsg]);
      } else {
        setMessages([...messages, userMessage]);
      }
    } finally {
      setIsLoading(false);
      setShowLoadingMessage(false);
      abortFuncs.current = abortFuncs.current.filter(
        (a) => a !== abortController,
      );
      setProcessMessages(messageStatus.Done);
      // console.log("makeApiRequestWithCosmosDB completed.");
    }

    return abortController.abort();
  };

  const clearStatus = appStateContext?.state.clearChatHistory;

  const clearChat = async () => {
    setClearingChat(true);
    if (
      appStateContext?.state.currentChat?.id &&
      appStateContext?.state.isCosmosDBAvailable.cosmosDB
    ) {
      let response = await historyClear(appStateContext?.state.currentChat.id);
      if (!response.ok) {
        setErrorMsg({
          title: "Error clearing current chat",
          subtitle:
            "Please try again. If the problem persists, please contact the site administrator.",
        });
        toggleErrorDialog();
      } else {
        appStateContext?.dispatch({
          type: "DELETE_CURRENT_CHAT_MESSAGES",
          payload: appStateContext?.state.currentChat.id,
        });
        appStateContext?.dispatch({
          type: "UPDATE_CHAT_HISTORY",
          payload: appStateContext?.state.currentChat,
        });
        setActiveCitation(undefined);
        setIsCitationPanelOpen(false);
        setMessages([]);
        voiceTurnCommittedRef.current = false;
        setVoiceSessionMessages([]);
      }
    }
    setClearingChat(false);
  };

  useEffect(() => {
    if (clearStatus == "clear") {
      clearChat();
      const clearChatHistory = "";
      appStateContext?.dispatch({
        type: "CLEAR_CHAT_HISTORY",
        payload: clearChatHistory,
      });
    }
  }, [clearStatus]);

  // if(appStateContext?.state.clearChatHistory == 'clear'){
  //
  //     clearChat();
  // }

  const newChat = () => {
    setProcessMessages(messageStatus.Processing);
    setMessages([]);
    voiceTurnCommittedRef.current = false;
    setVoiceSessionMessages([]);
    setIsCitationPanelOpen(false);
    setActiveCitation(undefined);
    appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: null });
    appStateContext?.dispatch({
      type: "ENTERED_QUESTION",
      payload: "",
    });
    appStateContext?.dispatch({ type: "SET_FILTERED_RESULTS", payload: [] });
    appStateContext?.dispatch({ type: "SET_SUBCATEGORY", payload: null });
    // setProcessMessages(messageStatus.Done);
  };

  const resetChatForSelectionChange = () => {
    setProcessMessages(messageStatus.Processing);
    setMessages([]);
    voiceTurnCommittedRef.current = false;
    setVoiceSessionMessages([]);
    setIsCitationPanelOpen(false);
    setActiveCitation(undefined);
    appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: null });
    appStateContext?.dispatch({ type: "ENTERED_QUESTION", payload: "" });
    appStateContext?.dispatch({ type: "SET_FILTERED_RESULTS", payload: [] });
    appStateContext?.dispatch({ type: "SET_SUBCATEGORY", payload: null });
  };

  const stopGenerating = () => {
    abortFuncs.current.forEach((a) => a.abort());
    setShowLoadingMessage(false);
    setIsLoading(false);
  };

  useEffect(() => {
    if (appStateContext?.state.currentChat) {
      setMessages(
        dedupeConsecutiveAssistantMessages(
          appStateContext.state.currentChat.messages,
        ),
      );
    } else {
      setMessages([]);
    }
  }, [appStateContext?.state.currentChat]);

  // useEffect(() => {
  //   const convId: any = appStateContext?.state.currentChat?.id;

  //   if (convId) {
  //     setisHistoryLoading(true);
  //     setMessages([]);

  //     const timeout = setTimeout(() => {
  //       const newMessages =
  //         appStateContext?.state.currentChat?.messages || [];
  //       setMessages(newMessages);
  //       setisHistoryLoading(false);
  //     }, 2000);

  //     return () => clearTimeout(timeout);
  //   } else {
  //     setMessages([]);
  //     setisHistoryLoading(false);
  //   }
  // }, [
  //   appStateContext?.state.currentChat?.id,
  //   appStateContext?.state.currentChat,
  // ]);

  // useEffect(() => {
  //   if ( appStateContext?.state.singleCurrentChat) {
  //     setMessages(appStateContext.state.singleCurrentChat );
  //   } else if (appStateContext?.state.currentChat?.messages) {
  //     setMessages(appStateContext.state.currentChat.messages);
  //     console.log("akarsh",appStateContext.state.singleCurrentChat)
  //   } else {
  //     setMessages([]);
  //   }
  // }, [appStateContext?.state.currentChat,appStateContext?.state.singleCurrentChat]);

  // useEffect(() => {
  //   const fetchChatHistory = async (): Promise<Conversation[] | null> => {
  //     try {
  //       const response = await historyList(
  //         appStateContext?.state.selectedAI || ""
  //       );

  //       appStateContext?.dispatch({
  //         type: "FETCH_CHAT_HISTORY",
  //         payload: response || null,
  //       });

  //       return response || null;
  //     } catch (error) {
  //       appStateContext?.dispatch({
  //         type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
  //         payload: ChatHistoryLoadingState.Fail,
  //       });

  //       appStateContext?.dispatch({
  //         type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
  //         payload: ChatHistoryLoadingState.Fail,
  //       });
  //       appStateContext?.dispatch({
  //         type: "FETCH_CHAT_HISTORY",
  //         payload: null,
  //       });
  //       console.error("Error fetching chat history:", error);
  //       return null;
  //     }
  //   };

  //   const getHistoryEnsure = async () => {
  //     appStateContext?.dispatch({
  //       type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
  //       payload: ChatHistoryLoadingState.Loading,
  //     });
  //     try {
  //       const response = await historyEnsure();
  //       if (response?.cosmosDB) {
  //         const chatHistory = await fetchChatHistory();
  //         appStateContext?.dispatch({
  //           type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
  //           payload: chatHistory
  //             ? ChatHistoryLoadingState.Success
  //             : ChatHistoryLoadingState.Fail,
  //         });
  //         appStateContext?.dispatch({
  //           type: "SET_COSMOSDB_STATUS",
  //           payload: response,
  //         });
  //       } else {
  //         appStateContext?.dispatch({
  //           type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
  //           payload: ChatHistoryLoadingState.Fail,
  //         });
  //         appStateContext?.dispatch({
  //           type: "SET_COSMOSDB_STATUS",
  //           payload: response,
  //         });
  //       }
  //     } catch (error) {
  //       appStateContext?.dispatch({
  //         type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
  //         payload: ChatHistoryLoadingState.Fail,
  //       });
  //       appStateContext?.dispatch({
  //         type: "SET_COSMOSDB_STATUS",
  //         payload: { cosmosDB: false, status: CosmosDBStatus.NotConfigured },
  //       });
  //     }
  //   };

  //   getHistoryEnsure();
  // }, []);

  useLayoutEffect(() => {
    const saveToDB = async (messages: ChatMessage[], id: string) => {
      const response = await historyUpdate(messages, id);
      return response;
    };

    const fetchChatHistory = async (): Promise<Conversation[] | null> => {
      try {
        const response = await historyList(
          appStateContext?.state.selectedAI || "",
        );

        appStateContext?.dispatch({
          type: "FETCH_CHAT_HISTORY",
          payload: response || null,
        });

        return response || null;
      } catch (error) {
        appStateContext?.dispatch({
          type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
          payload: ChatHistoryLoadingState.Fail,
        });
        appStateContext?.dispatch({
          type: "FETCH_CHAT_HISTORY",
          payload: null,
        });
        console.error("Error fetching chat history:", error);
        return null;
      }
    };

    if (
      appStateContext &&
      appStateContext.state.currentChat &&
      processMessages === messageStatus.Done
    ) {
      if (appStateContext.state.isCosmosDBAvailable.cosmosDB) {
        if (!appStateContext?.state.currentChat?.messages) {
          console.error("Failure fetching current chat state.");
          return;
        }
        setIsUpdatingHistory(true);
        saveToDB(
          appStateContext.state.currentChat.messages,
          appStateContext.state.currentChat.id,
        )
          .then(async (res) => {
            if (res.ok) {
              await fetchChatHistory();
            } else {
              let errorMessage =
                "An error occurred. Answers can't be saved at this time. If the problem persists, please contact the site administrator.";
              let errorChatMsg: ChatMessage = {
                id: uuid(),
                role: "error",
                content: errorMessage,
                date: new Date().toISOString(),
                createdAt: undefined,
              };
              setMessages(
                dedupeConsecutiveAssistantMessages([
                  ...(appStateContext?.state.currentChat?.messages || []),
                  errorChatMsg,
                ]),
              );
            }
            return res as Response;
          })
          .catch((err) => {
            console.error("Error: ", err);
            return {
              ...new Response(),
              ok: false,
              status: 500,
            } as Response;
          })
          .finally(() => {
            setIsUpdatingHistory(false);
          });
      }

      // Still keep updating reducer + messages
      appStateContext?.dispatch({
        type: "UPDATE_CHAT_HISTORY",
        payload: appStateContext.state.currentChat,
      });

      setMessages(
        dedupeConsecutiveAssistantMessages(
          appStateContext.state.currentChat.messages,
        ),
      );
      setProcessMessages(messageStatus.NotRunning);
    }
  }, [processMessages]);

  useEffect(() => {
    getUserInfoList();
  }, []);

  useLayoutEffect(() => {
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [
    showLoadingMessage,
    processMessages,
    liveVoiceTranscript,
    liveAITranscript,
    voiceStatus,
  ]);

  const onShowCitation = (citation: Citation) => {
    // if (citation.url) {
    //    window.open(citation.url, "_blank");
    // }
    // else
    // {
    setActiveCitation([
      citation.content,
      citation.id,
      citation.title ?? "",
      citation.filepath ?? "",
      "",
      "",
    ]);
    setIsCitationPanelOpen(true);
    //}
  };

  const parseCitationFromMessage = (message: ChatMessage) => {
    if (message?.role && message?.role === "tool") {
      try {
        const toolMessage = JSON.parse(message.content) as ToolMessageContent;
        return toolMessage.citations;
      } catch {
        return [];
      }
    }
    return [];
  };

  const sideBarEnable = appStateContext?.state.isChatHistoryOpen;

  const disabledButton = () => {
    return (
      isLoading ||
      (messages && messages.length === 0) ||
      clearingChat ||
      appStateContext?.state.chatHistoryLoadingState ===
        ChatHistoryLoadingState.Loading
    );
  };

  const [isToggled, setIsToggled] = useState(() => {
    const saved = localStorage.getItem("dark-theme-enabled");
    return saved !== null ? saved === "true" : true;
  });

  useEffect(() => {
    if (isAdminPage) {
      document.body.classList.remove("dark-theme");

      return;
    }

    if (isToggled) {
      document.body.classList.add("dark-theme");
    } else {
      document.body.classList.remove("dark-theme");
    }
  }, [isToggled, isAdminPage]);

  // console.log(messages.content)

  //     const handleDownload = () => {
  //     const a = document.createElement("a");
  //     a.href = answer.pdfurl;
  //     a.target = "_blank";
  //     a.rel = "noopener noreferrer";
  //     document.body.appendChild(a);
  //     a.click();
  //     document.body.removeChild(a);
  //   };

  // const uuidExists = messages.length > 0 && messages[messages.length - 1].id;
  const modelOnly = appStateContext?.state.modelCategory || [];
  const appOnly = appStateContext?.state.appCategory || [];
  const selectedAI = appStateContext?.state.selectedAI;
  const fetchedModelHistory = appStateContext?.state.filteredChatHistoryByModel;

  // const loadingState =
  //   appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading ||
  //   !fetchedModelHistory ||
  //   modelOnly.length === 0 ||
  //   appOnly.length === 0 ||
  //   !selectedAI;

  const isFetching =
    appStateContext?.state.chatHistoryLoadingState ===
    ChatHistoryLoadingState.Loading;

  const hasData =
    fetchedModelHistory &&
    modelOnly.length > 0 &&
    appOnly.length > 0 &&
    !!selectedAI &&
    !beforeDash &&
    !afterDash;

  const loadingState = isFetching || !hasData;

  // console.log("fetchedModelHistory", fetchedModelHistory);

  // if (fetchedModelHistory && fetchedModelHistory.length > 0) {
  //   console.log("Fetched model history:", fetchedModelHistory);
  // } else {
  //   console.log("No model history found.");
  // }

  // const loadingstate =
  // appStateContext?.state.chatHistoryLoadingState;

  const chatHistroyLoading = appStateContext?.state.isFetchingMessages;
  // appStateContext?.state.isFetchingMessages ;
  //    ||
  // appStateContext?.state.isFetchingMessages;

  // const visiblemodel = appOnly
  //   ?.map((e) => e.Name)
  //   ?.includes(appStateContext?.state.selectedAI);

  useEffect(() => {
    if (!selectedAI) {
      setSelectedType(null);
      previousSelectedAIRef.current = null;
      return;
    }

    const previousSelectedAI = previousSelectedAIRef.current;
    const hasSelectionChanged =
      previousSelectedAI !== null && previousSelectedAI !== selectedAI;

    if (selectedAI === "MappingBuddy") {
      setSelectedType("model");
      if (hasSelectionChanged) {
        resetChatForSelectionChange();
      }
      previousSelectedAIRef.current = selectedAI;
      return;
    }

    const isModel = modelOnly.some((m: any) => m.Name === selectedAI);
    const isApp = appOnly.some((a: any) => a.Name === selectedAI);

    if (isModel) {
      setSelectedType("model");
      if (hasSelectionChanged) {
        resetChatForSelectionChange();
      } else {
        setIsCitationPanelOpen(false);
        setActiveCitation(undefined);
      }
    } else if (isApp) {
      setSelectedType("app");
      if (hasSelectionChanged) {
        resetChatForSelectionChange();
      } else {
        setIsCitationPanelOpen(false);
        setActiveCitation(undefined);
      }
    } else {
      setSelectedType(null);
      // setDisplayName("Lipi");
    }

    previousSelectedAIRef.current = selectedAI;
  }, [selectedAI, modelOnly, appOnly]);

  const renderVoiceAssistantAnswer = (message: ChatMessage) => (
    <div key={message.id} className={styles.chatMessageGpt}>
      <div className={`${styles.aiIcon} ${styles.GPTFamily}`}>
        <span>AI</span>
      </div>
      <Answer
        answer={{
          answer: message.content,
          citations: [],
          message_id: message.id,
          chat_Contnet: message.content,
          isLoading: false,
          userQuestion: liveVoiceTranscriptRef.current,
          selectedModel: "Lipi One",
          isImage: message.isImage || false,
          imageUrl: message.imageUrl || message.image_url || null,
          image_url: message.image_url,
        }}
        onCitationClicked={() => null}
      />
    </div>
  );

  const renderLiveVoicePreview = () => (
    <>
      {[
        ...visibleVoiceSessionMessages,
        ...(liveVoiceTranscript.trim()
          ? [
              {
                id: "live-user-preview",
                role: "user",
                content: liveVoiceTranscript,
                date: "",
                createdAt: undefined,
              } as ChatMessage,
            ]
          : []),
        ...(shouldRenderLiveAIPreview
          ? [
              {
                id: "live-ai-preview",
                role: "assistant",
                content: liveAITranscript,
                date: "",
                createdAt: undefined,
              } as ChatMessage,
            ]
          : []),
      ].map((message) =>
        message.role === "user" ? (
          <div key={message.id} className={styles.chatMessageUser} tabIndex={0}>
            <div className={styles.chatMessageUserMessage}>
              {message.content}
            </div>
          </div>
        ) : (
          renderVoiceAssistantAnswer(message)
        ),
      )}
      {!liveAITranscript.trim() &&
        voiceStatus === "thinking" &&
        liveVoiceTranscript.trim() && (
          <div className={styles.chatMessageGpt}>
            <div className={`${styles.aiIcon} ${styles.GPTFamily}`}>
              <span>AI</span>
            </div>
            <div className={styles.chatVoiceAgentPreview}>Processing...</div>
          </div>
        )}
    </>
  );

  return (
    <div className={styles.container} role="main">
      {/* <button onClick={toggleTheme}>
Toggle to {theme==='light'?'Dark':'Light'}
      </button> */}
      {isFetching ? (
        <Stack className={styles.chatEmptyState}>
          <ChatLoader
            className={styles.loaderContainerChat}
            spinnerClass={styles.loaderchat}
          />

          {/* <ShieldLockRegular
            className={styles.chatIcon}
            style={{ color: "darkorange", height: "200px", width: "200px" }}
          />
          <h1 className={styles.chatEmptyStateTitle}>
            Authentication Not Configured
          </h1>
          <h2 className={styles.chatEmptyStateSubtitle}>
            This app does not have authentication configured. Please add an
            identity provider by finding your app in the
            <a href="https://portal.azure.com/" target="_blank">
              {" "}
              Azure Portal{" "}
            </a>
            and following
            <a
              href="https://learn.microsoft.com/en-us/azure/app-service/scenario-secure-app-authentication-app-service#3-configure-authentication-and-authorization"
              target="_blank"
            >
              {" "}
              these instructions
            </a>
            .
          </h2>
          <h2
            className={styles.chatEmptyStateSubtitle}
            style={{ fontSize: "20px" }}
          >
            <strong>
              Authentication configuration takes a few minutes to apply.{" "}
            </strong>
          </h2>
          <h2
            className={styles.chatEmptyStateSubtitle}
            style={{ fontSize: "20px" }}
          >
            <strong>
              If you deployed in the last 10 minutes, please wait and reload the
              page after 10 minutes.
            </strong>
          </h2> */}
        </Stack>
      ) : (
        //  <Stack horizontal className={`${styles.chatRoot} bg-body text-body `}>

        <Stack horizontal className={styles.chatRoot}>
          {/* {appStateContext?.state.isChatHistoryOpen &&
            appStateContext?.state.isCosmosDBAvailable?.status !==
              CosmosDBStatus.NotConfigured &&} */}
          <ChatHistoryPanel onNewChat={newChat} />
          <div
            className={`${styles.chatContainer} ${
              !sideBarEnable ? styles.chatContainer_active : ""
            }`}
          >
            {/* <Layout onNewChat={newChat} /> */}
            <Layout
              onNewChat={newChat}
              isToggled={isToggled}
              setIsToggled={setIsToggled}
            />

            <div className={styles.chatInnerContainer}>
              <div className="chatInnerWrapper">
                {chatHistroyLoading ? (
                  <ChatLoader
                    className={styles.loaderContainer}
                    spinnerClass={styles.loader}
                  />
                ) : !messages || messages.length < 1 ? (
                  hasLiveVoicePreview ? (
                    <div
                      className={styles.chatMessageStream}
                      style={{ marginBottom: isLoading ? "40px" : "0px" }}
                      role="log"
                    >
                      <div className={styles.chatMessageStream_innerWrapper}>
                        {renderLiveVoicePreview()}
                        <div ref={chatMessageStreamEnd} />
                      </div>
                    </div>
                  ) : (
                    <Stack className={styles.chatEmptyState}>
                      {/* <img
                                    src={Azure}
                                    className={styles.chatIcon}
                                    aria-hidden="true"
                                /> */}
                      {/* <img
                  src={Genie}
                  className={styles.chatIcon}
                  aria-hidden="true"
                /> */}
                      <h1 className={styles.chatEmptyStateTitle}>
                        <span>{beforeDash}</span>
                        {afterDash && (
                          <span className={styles.chatEmptyStateques}>
                            {afterDash}
                          </span>
                        )}
                      </h1>
                      {/* <h2 className={styles.chatEmptyStateSubtitle}>
                  This chatbot is configured on GPT-4o to answer your questions
                </h2> */}
                    </Stack>
                  )
                ) : (
                  <div
                    className={styles.chatMessageStream}
                    style={{ marginBottom: isLoading ? "40px" : "0px" }}
                    role="log"
                  >
                    <div className={styles.chatMessageStream_innerWrapper}>
                      {messages.map((answer, index) => (
                        <>
                          {answer.role === "user" ? (
                            <div
                              className={styles.chatMessageUser}
                              tabIndex={0}
                            >
                              {answer.pdfurl ? (
                                <div
                                  className={styles.chatMessageUser}
                                  tabIndex={0}
                                >
                                  {answer.pdfurl && (
                                    <div
                                      className={styles.chatMessageUser}
                                      tabIndex={0}
                                    >
                                      {isImageUrl(answer.pdfurl) ? (
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "end",
                                              width: "100%",
                                            }}
                                          >
                                            <div
                                              className={styles.imageContainer}
                                            >
                                              <img
                                                src={answer.pdfurl}
                                                alt="uploaded"
                                                style={{
                                                  maxWidth: "100%",
                                                  maxHeight: "auto",
                                                  borderRadius: "5px",
                                                }}
                                              />
                                            </div>
                                          </div>
                                          <div
                                            className={styles.imageDownloadRow}
                                          >
                                            <a
                                              className={
                                                styles.imageDownloadLink
                                              }
                                              href={answer.pdfurl}
                                              download
                                              title="Download file"
                                            >
                                              <DownloadIcon fontSize="small" />
                                            </a>
                                          </div>
                                          {answer.content && (
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "end",
                                              }}
                                            >
                                              <div
                                                className={`${styles.PdfchatMessageUserMessage} ${styles.imagePromptMessage}`}
                                              >
                                                {answer.content}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                          }}
                                        >
                                          <div className={styles.pdfInfo}>
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              viewBox="0 0 36 36"
                                              fill="none"
                                              className="h-10 w-10 flex-shrink-0"
                                              width="36"
                                              height="36"
                                            >
                                              <rect
                                                width="36"
                                                height="36"
                                                rx="6"
                                                fill="rgb(219, 33, 40)"
                                              />
                                              <path
                                                d="M19.6663 9.66663H12.9997C12.5576 9.66663 12.1337 9.84222 11.8212 10.1548C11.5086 10.4673 11.333 10.8913 11.333 11.3333V24.6666C11.333 25.1087 11.5086 25.5326 11.8212 25.8451C12.1337 26.1577 12.5576 26.3333 12.9997 26.3333H22.9997C23.4417 26.3333 23.8656 26.1577 24.1782 25.8451C24.4907 25.5326 24.6663 25.1087 24.6663 24.6666V14.6666L19.6663 9.66663Z"
                                                stroke="white"
                                                strokeWidth="1.66667"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M19.667 9.66663V14.6666H24.667"
                                                stroke="white"
                                                strokeWidth="1.66667"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M21.3337 18.8334H14.667"
                                                stroke="white"
                                                strokeWidth="1.66667"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M21.3337 22.1666H14.667"
                                                stroke="white"
                                                strokeWidth="1.66667"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M16.3337 15.5H15.5003H14.667"
                                                stroke="white"
                                                strokeWidth="1.66667"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                            <div
                                              className={styles.pdfFileName}
                                              title={getAttachmentDisplayName(
                                                answer.pdfurl,
                                              )}
                                            >
                                              {getTrimmedAttachmentName(
                                                answer.pdfurl,
                                              )}
                                            </div>

                                            <a
                                              // onClick={handleDownload}
                                              key={index}
                                              href={answer.pdfurl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              download
                                              title="Download file"
                                              className={styles.pdfDownloadLink}
                                            >
                                              <DownloadIcon
                                                style={{ cursor: "pointer" }}
                                              />
                                            </a>
                                          </div>

                                          {answer.content && (
                                            <div
                                              className={
                                                styles.PdfchatMessageUserMessage
                                              }
                                            >
                                              {answer.content}
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      <div className={styles.messageStatus}>
                                        {(answer.date || answer.createdAt) && (
                                          <MessageTime
                                            timestamp={
                                              new Date(
                                                answer.date || answer.createdAt,
                                              )
                                            }
                                          />
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div
                                  className={styles.chatMessageUser}
                                  tabIndex={0}
                                >
                                  {/* <div className={styles.voiceElemContainer}>
                                  <div className={styles.chatMessageUserMessage}>                                   
                                   {answer.content}
                                  </div>
                                    <i className={styles.voiceIconWrapper}>
                                    <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 24 24" height="100%" width="100%" xmlns="http://www.w3.org/2000/svg"><path d="M8 12c2.28 0 4-1.72 4-4s-1.72-4-4-4-4 1.72-4 4 1.72 4 4 4zm0-6c1.178 0 2 .822 2 2s-.822 2-2 2-2-.822-2-2 .822-2 2-2zm1 7H7c-2.757 0-5 2.243-5 5v1h2v-1c0-1.654 1.346-3 3-3h2c1.654 0 3 1.346 3 3v1h2v-1c0-2.757-2.243-5-5-5zm9.364-10.364L16.95 4.05C18.271 5.373 19 7.131 19 9s-.729 3.627-2.05 4.95l1.414 1.414C20.064 13.663 21 11.403 21 9s-.936-4.663-2.636-6.364z"></path><path d="M15.535 5.464 14.121 6.88C14.688 7.445 15 8.198 15 9s-.312 1.555-.879 2.12l1.414 1.416C16.479 11.592 17 10.337 17 9s-.521-2.592-1.465-3.536z"></path></svg>
                                    </i> 
                                  </div> */}

                                  <div
                                    className={styles.chatMessageUserMessage}
                                  >
                                    {answer.content}
                                  </div>

                                  <div className={styles.messageStatus}>
                                    {(answer.date || answer.createdAt) && (
                                      <MessageTime
                                        timestamp={
                                          new Date(
                                            answer.date || answer.createdAt,
                                          )
                                        }
                                      />
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : answer.role === "assistant" ? (
                            <div className={styles.chatMessageGpt}>
                              {/*@ts-ignore*/}

                              <div
                                className={`${styles.aiIcon} ${
                                  styles[
                                    getResponseBadgeClassName(
                                      getResponseServiceName(
                                        answer,
                                        appStateContext?.state
                                          .isLiveSeachActive,
                                      ),
                                    )
                                  ]
                                }`}
                              >
                                {renderResponseBadgeContent(
                                  getResponseServiceName(
                                    answer,
                                    appStateContext?.state.isLiveSeachActive,
                                  ),
                                )}
                              </div>

                              <Answer
                                answer={{
                                  answer: answer.content,
                                  citations: parseCitationFromMessage(
                                    messages[index - 1],
                                  ),
                                  message_id: answer.id,
                                  chat_Contnet: answer.content,
                                  isLoading,
                                  userQuestion: currentQuestion,
                                  /*@ts-ignore*/
                                  fileUrl: answer.files,
                                  selectedModel: getResponseServiceName(
                                    answer,
                                    appStateContext?.state.isLiveSeachActive,
                                  ),
                                  // 🖼️ IMAGE PROPERTIES - Reference Format
                                  isImage: answer.isImage || false,
                                  imageUrl:
                                    answer.imageUrl || answer.image_url || null,

                                  // uuidContent: uuidContent
                                }}
                                onCitationClicked={(c) => onShowCitation(c)}
                              />
                              <div className={styles.messageStatus}>
                                {(answer.date || answer.createdAt) && (
                                  <MessageTime
                                    timestamp={
                                      new Date(answer.date || answer.createdAt)
                                    }
                                  />
                                )}
                              </div>
                              {/* @ts-ignore */}

                              {
                                /* @ts-ignore */
                                answer.ai_service === "PrivacyPolicyBOT" &&
                                  ((Array.isArray(answer.followups) &&
                                    answer.followups.some(
                                      (f: string) => f && f.trim() !== "",
                                    )) ||
                                    (typeof answer.followups === "string" &&
                                      answer.followups.trim() !== "")) && (
                                    <h6 className={styles.SuggestionHeading}>
                                      You might be interested in:
                                    </h6>
                                  )
                              }

                              {/* {showFollowups && followUps.length > 0 && ( */}
                              {/*@ts-ignore*/}
                              {/* Debug */}

                              {[
                                "PrivacyPolicyBOT",
                                "LipiResume",
                                "Finance",
                              ].includes(
                                /*@ts-ignore*/
                                answer?.ai_service,
                              ) &&
                                /*@ts-ignore*/
                                answer?.followups &&
                                /*@ts-ignore*/
                                (Array.isArray(answer.followups)
                                  ? /*@ts-ignore*/
                                    answer.followups
                                      .filter(
                                        (f: string) => f && f.trim() !== "",
                                      )
                                      .map(
                                        (followup: string, index: number) => (
                                          <div
                                            key={index}
                                            className={
                                              styles.followupSuggestions
                                            }
                                            style={{ display: "block" }}
                                          >
                                            <button
                                              onClick={() =>
                                                setSelectedFollowup(followup)
                                              }
                                              className={styles.followupButton}
                                              type="button"
                                            >
                                              {followup}
                                            </button>
                                          </div>
                                        ),
                                      )
                                  : /*@ts-ignore*/
                                    typeof answer.followups === "string" &&
                                    /*@ts-ignore*/
                                    answer.followups.trim() !== "" && (
                                      <div
                                        className={styles.followupSuggestions}
                                        style={{ display: "block" }}
                                      >
                                        <button
                                          onClick={() =>
                                            setSelectedFollowup(
                                              /*@ts-ignore*/
                                              answer.followups as string,
                                            )
                                          }
                                          className={styles.followupButton}
                                          type="button"
                                        >
                                          {/*@ts-ignore*/}
                                          {answer.followups}
                                        </button>
                                      </div>
                                    ))}

                              {/* )} */}
                            </div>
                          ) : answer.role === "error" ? (
                            <div className={styles.chatMessageError}>
                              <Stack
                                horizontal
                                className={styles.chatMessageErrorContent}
                              >
                                <ErrorCircleRegular
                                  className={styles.errorIcon}
                                  style={{ color: "rgba(182, 52, 67, 1)" }}
                                />
                                <span>Error</span>
                              </Stack>
                              <span
                                className={styles.chatMessageErrorContent_inner}
                              >
                                {answer.content}
                              </span>
                            </div>
                          ) : null}
                        </>
                      ))}
                      {showLoadingMessage && (
                        <>
                          <div className={styles.chatMessageGpt}>
                            <div className={styles.answerloaderContainer}>
                              <span className={styles.lipitext}>
                                <svg
                                  width="19"
                                  height="11"
                                  viewBox="0 0 58 32"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    id="logopath1"
                                    d="M55.0589 4.27963C54.4429 4.27963 53.9133 4.0797 53.4702 3.67984C53.0379 3.27997 52.8218 2.77204 52.8218 2.15603C52.8218 1.54002 53.0379 1.02668 53.4702 0.616008C53.9133 0.205336 54.4429 0 55.0589 0C55.6965 0 56.2368 0.205336 56.6799 0.616008C57.123 1.02668 57.3446 1.54002 57.3446 2.15603C57.3446 2.73961 57.123 3.24215 56.6799 3.66363C56.2368 4.0743 55.6965 4.27963 55.0589 4.27963ZM56.9231 22.4843C56.9231 23.5228 56.0812 24.3647 55.0426 24.3647C54.0041 24.3647 53.1622 23.5228 53.1622 22.4843V9.64539C53.1622 8.60684 54.0041 7.76494 55.0426 7.76494C56.0812 7.76494 56.9231 8.60684 56.9231 9.64538V22.4843Z"
                                  ></path>
                                  <path
                                    id="logopath2"
                                    d="M37.2763 21.3745C37.2683 21.3616 37.2541 21.3535 37.2389 21.3535C37.2148 21.3535 37.1952 21.3731 37.1952 21.3973V30.0221C37.1952 31.1145 36.3096 32 35.2173 32C34.1249 32 33.2393 31.1144 33.2393 30.0221V8.38873C33.2393 7.29634 34.1249 6.4108 35.2173 6.4108H35.6547C36.5055 6.4108 37.1952 7.1005 37.1952 7.95128V9.44848C37.1952 9.47239 37.2146 9.49177 37.2385 9.49177C37.2539 9.49177 37.2682 9.48341 37.276 9.47009C38.6284 7.1567 40.6021 6 43.1972 6C45.4138 6 47.1417 6.78165 48.3807 8.34496C49.6198 9.90827 50.2393 12.0079 50.2393 14.6438C50.2393 17.5651 49.5402 19.9043 48.142 21.6616C46.7438 23.4189 44.8341 24.2976 42.4128 24.2976C40.1928 24.2976 38.4806 23.3232 37.2763 21.3745ZM37.1611 14.4384V16.6122C37.1611 17.8903 37.5646 18.9743 38.3717 19.8644C39.1902 20.7544 40.2246 21.1995 41.475 21.1995C42.9414 21.1995 44.0895 20.6289 44.9194 19.4878C45.7605 18.3353 46.1811 16.7321 46.1811 14.6781C46.1811 12.955 45.789 11.6085 45.0046 10.6386C44.2316 9.65723 43.1801 9.16656 41.8502 9.16656C40.4406 9.16656 39.3038 9.66864 38.4399 10.6728C37.5874 11.677 37.1611 12.9322 37.1611 14.4384Z"
                                    fill="#0D0D0D"
                                  ></path>
                                  <path
                                    id="logopath3"
                                    d="M25.9636 23.0058C25.9623 22.9454 25.9369 22.8879 25.8925 22.8452C25.8481 22.8025 25.7882 22.778 25.7254 22.7768L18.6226 22.7775C18.617 22.7775 18.6123 22.7745 18.6067 22.7745C18.5738 22.7726 18.5413 22.767 18.5098 22.7577C18.4886 22.7538 18.4678 22.7484 18.4475 22.7413C18.418 22.7269 18.3904 22.7091 18.3653 22.6882C18.3486 22.6776 18.3327 22.6659 18.3177 22.6531C18.2957 22.6284 18.2766 22.6014 18.2609 22.5726C18.2503 22.5569 18.2407 22.5406 18.2324 22.5237C18.2296 22.5172 18.222 22.5138 18.2212 22.5054L16.095 16.4468C15.987 16.0827 15.9708 15.699 16.0478 15.3277C16.1248 14.9563 16.2927 14.608 16.5376 14.3114L21.3062 8.54369C21.4474 8.37108 21.6283 8.2322 21.8346 8.13792C22.0409 8.04364 22.2671 7.99651 22.4956 8.0002L22.4979 8.00249L22.5003 8.0002L22.5087 8.00211C22.7074 7.99972 22.9047 8.03565 23.0888 8.10775C23.2729 8.17986 23.4401 8.28669 23.5805 8.42194C23.6215 8.46137 23.6601 8.50304 23.696 8.54675L28.4638 14.313C28.7101 14.6124 28.8782 14.9642 28.9541 15.3389C29.03 15.7136 29.0115 16.1003 28.9001 16.4667L27.8977 19.3235C27.8803 19.3742 27.8527 19.4211 27.8163 19.4615C27.78 19.5019 27.7357 19.535 27.6861 19.5588C27.6364 19.5827 27.5823 19.5969 27.5269 19.6006C27.4715 19.6043 27.4159 19.5974 27.3632 19.5803C27.3106 19.5632 27.262 19.5363 27.2202 19.5011C27.1785 19.4659 27.1444 19.4231 27.1199 19.3752C27.0954 19.3273 27.081 19.2752 27.0776 19.2219C27.0741 19.1686 27.0817 19.1152 27.0998 19.0647L28.0962 16.2259C28.1674 15.9856 28.1781 15.7326 28.1273 15.4876C28.0766 15.2426 27.9659 15.0128 27.8044 14.8171L23.0414 9.04787C23.007 9.00751 22.9677 8.97125 22.9243 8.93986V14.1122C23.4502 14.2126 23.9185 14.4976 24.238 14.9116C24.5575 15.3256 24.7054 15.8391 24.6529 16.3521C24.6004 16.8651 24.3513 17.3411 23.954 17.6873C23.5567 18.0335 23.0397 18.2253 22.5035 18.2253C21.9673 18.2253 21.4503 18.0335 21.053 17.6873C20.6557 17.3411 20.4066 16.8651 20.3541 16.3521C20.3016 15.8391 20.4495 15.3256 20.769 14.9116C21.0885 14.4976 21.5568 14.2126 22.0827 14.1122L22.0823 8.93872C22.0391 8.97068 22.0004 9.00788 21.9672 9.0494L17.1978 14.8141C17.0377 15.0072 16.9273 15.2339 16.8758 15.4758C16.8243 15.7177 16.833 15.968 16.9013 16.206L18.9259 21.9692L25.727 21.9669C25.8688 21.9668 26.0092 21.9935 26.1402 22.0456C26.2712 22.0977 26.3903 22.1742 26.4905 22.2706C26.5908 22.367 26.6703 22.4814 26.7245 22.6074C26.7787 22.7333 26.8065 22.8683 26.8064 23.0046L26.8068 24.9599C26.8067 25.2352 26.6929 25.4992 26.4904 25.6939C26.2879 25.8885 26.0134 25.9979 25.727 25.998L16.8519 25.9999C16.796 26.0009 16.7405 25.9911 16.6885 25.9712C16.6366 25.9513 16.5893 25.9217 16.5494 25.884C16.5095 25.8464 16.4778 25.8015 16.4562 25.7519C16.4345 25.7023 16.4233 25.6491 16.4233 25.5954C16.4233 25.5416 16.4344 25.4884 16.456 25.4388C16.4776 25.3892 16.5092 25.3443 16.5491 25.3066C16.589 25.2689 16.6362 25.2392 16.6881 25.2193C16.7401 25.1993 16.7956 25.1895 16.8515 25.1904L25.7266 25.19C25.7579 25.1902 25.789 25.1844 25.818 25.1729C25.8469 25.1615 25.8733 25.1446 25.8954 25.1233C25.9176 25.102 25.9351 25.0767 25.947 25.0489C25.9589 25.021 25.965 24.9912 25.9648 24.961L25.9636 23.0058ZM21.575 17.0354C21.7586 17.2119 21.9926 17.3322 22.2473 17.3809C22.502 17.4296 22.7661 17.4046 23.006 17.309C23.246 17.2135 23.4511 17.0517 23.5953 16.8441C23.7396 16.6364 23.8167 16.3924 23.8167 16.1427C23.8167 15.893 23.7396 15.6489 23.5953 15.4413C23.4511 15.2336 23.246 15.0718 23.006 14.9763C22.7661 14.8807 22.502 14.8557 22.2473 14.9044C21.9926 14.9531 21.7586 15.0734 21.575 15.2499C21.329 15.4868 21.1908 15.8079 21.1908 16.1427C21.1908 16.4774 21.329 16.7985 21.575 17.0354Z"
                                    fill="#DA2128"
                                    stroke="#DA2128"
                                    stroke-width="0.9"
                                  ></path>
                                  <circle
                                    id="logopathcircle"
                                    cx="23.2471"
                                    cy="2.24705"
                                    r="2.24705"
                                    fill="#DA2128"
                                  ></circle>
                                  <path
                                    id="logopath5"
                                    d="M14 25.2513C14 26.2171 13.2171 27 12.2513 27H2C0.895431 27 0 26.1046 0 25V2C0 0.895431 0.895431 0 2 0H2.19647C3.30104 0 4.19647 0.895431 4.19647 2V21.5026C4.19647 22.6071 5.0919 23.5026 6.19647 23.5026H12.2513C13.2171 23.5026 14 24.2855 14 25.2513Z"
                                    fill="#0D0D0D"
                                  ></path>
                                </svg>
                              </span>
                              <div className={styles.answerloader}></div>
                            </div>
                            <div className={styles.chatMessageGpt_inner}>
                              {/* <Answer
                                answer={{
                                  answer: `${
                                    IsdalleSelcted
                                      ? "Generating Image ..."
                                      : "Generating answer"
                                  }`,

                                  
                                  citations: [],
                                  selectedModel: "",
                                }}
                                onCitationClicked={() => null}
                              /> */}
                            </div>
                          </div>
                        </>
                      )}
                      {hasLiveVoicePreview && renderLiveVoicePreview()}
                      <div ref={chatMessageStreamEnd} />
                    </div>
                  </div>
                )}

                <Stack horizontal className={styles.chatInput}>
                  <div className={styles.chatInput_innerwrapper}>
                    {isLoading && (
                      <Stack
                        horizontal
                        className={styles.stopGeneratingContainer}
                        role="button"
                        aria-label="Stop generating"
                        tabIndex={0}
                        onClick={stopGenerating}
                        onKeyDown={(e) =>
                          e.key === "Enter" || e.key === " "
                            ? stopGenerating()
                            : null
                        }
                      >
                        {/* <SquareRegular
                          className={styles.stopGeneratingIcon}
                          aria-hidden="true"
                        /> */}
                        <svg
                          className={styles.stopGeneratingIcon}
                          aria-hidden="true"
                          stroke="currentColor"
                          fill="currentColor"
                          strokeWidth="0"
                          viewBox="0 0 448 512"
                          height="1em"
                          width="1em"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48z"></path>
                        </svg>
                        <span
                          className={styles.stopGeneratingText}
                          aria-hidden="true"
                        >
                          Stop generating
                        </span>
                      </Stack>
                    )}
                    <Stack>
                      {appStateContext?.state.isCosmosDBAvailable?.status !==
                        CosmosDBStatus.NotConfigured && (
                        <CommandBarButton
                          role="button"
                          styles={{
                            icon: {
                              color: "#FFFFFF !important",
                            },
                            root: {
                              color: "#FFFFFF",
                              background: "#DA2129",
                            },
                            rootHovered: {
                              background: "#DA2129",
                              color: "#FFFFFF !important",
                            },
                            rootDisabled: {
                              background: "#DA2129",
                            },
                          }}
                          className={styles.newChatIcon}
                          iconProps={{ iconName: "HomeSolid" }}
                          onClick={newChat}
                          //disabled={disabledButton()}
                          aria-label="start a new chat button"
                        />
                      )}
                      {/*<CommandBarButton
                                    role="button"
                                    styles={{ 
                                        icon: { 
                                            color: '#FFFFFF',
                                        },
                                        root: {
                                            color: '#FFFFFF',
                                            background: disabledButton() ? "#BDBDBD" : "radial-gradient(109.81% 107.82% at 100.1% 90.19%, #0F6CBD 33.63%, #2D87C3 70.31%, #8DDDD8 100%)",
                                            cursor: disabledButton() ? "" : "pointer"
                                        },
                                    }}
                                    className={appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured ? styles.clearChatBroom : styles.clearChatBroomNoCosmos}
                                    iconProps={{ iconName: 'Broom' }}
                                    onClick={appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured ? clearChat : newChat}
                                    disabled={disabledButton()}
                                    aria-label="clear chat button"
                                />*/}
                      <Dialog
                        hidden={hideErrorDialog}
                        onDismiss={handleErrorDialogClose}
                        dialogContentProps={errorDialogContentProps}
                        modalProps={modalProps}
                      ></Dialog>
                    </Stack>

                    <QuestionInput
                      clearOnSend
                      placeholder="Type a new question..."
                      disabled={
                        isLoading ||
                        appStateContext?.state.isCosmosDBAvailable?.status ===
                          CosmosDBStatus.NotConfigured
                      }
                      FollowUpsProp={selectedFollowup}
                      onSend={(question, fileurl, id) => {
                        appStateContext?.state.isCosmosDBAvailable?.cosmosDB
                          ? makeApiRequestWithCosmosDB(question, fileurl, id)
                          : makeApiRequestWithoutCosmosDB(question, id);
                      }}
                      conversationId={
                        appStateContext?.state.currentChat?.id
                          ? appStateContext?.state.currentChat?.id
                          : undefined
                      }
                      onNewChat={newChat}
                      voiceStatus={voiceStatus}
                      toggleSession={toggleSession}
                    />
                  </div>
                </Stack>

                {shouldShowPromptPanel && (
                  <PromtPanel allCategoryData={allCategoryData} />
                )}
              </div>
              <Copyright />
            </div>
          </div>
          {messages &&
            messages.length > 0 &&
            isCitationPanelOpen &&
            activeCitation && (
              <Stack.Item
                className={styles.citationPanel}
                tabIndex={0}
                role="tabpanel"
                aria-label="Citations Panel"
              >
                <Stack
                  aria-label="Citations Panel Header Container"
                  horizontal
                  className={styles.citationPanelHeaderContainer}
                  horizontalAlign="space-between"
                  verticalAlign="center"
                >
                  <span
                    aria-label="Citations"
                    className={styles.citationPanelHeader}
                  >
                    Citations
                  </span>
                  <IconButton
                    iconProps={{ iconName: "Cancel" }}
                    aria-label="Close citations panel"
                    onClick={() => setIsCitationPanelOpen(false)}
                  />
                </Stack>
                <h5 className={styles.citationPanelTitle} tabIndex={0}>
                  {activeCitation[2]}
                </h5>
                <div tabIndex={0}>
                  <ReactMarkdown
                    linkTarget="_blank"
                    className={styles.citationPanelContent}
                    children={activeCitation[0]}
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  />
                </div>
              </Stack.Item>
            )}

          {/* {(appStateContext?.state.isPromtPanelOpen) && <PromtPanel/>} */}
        </Stack>
      )}
    </div>
  );
};

export default Chat;
